/**
 * Arthaus Motion — scroll reveal + parallax
 * Phase: aesthetic buildout
 *
 * Markup API (no config needed):
 *   data-reveal="fade-up|fade-down|fade|scale|slide-left|slide-right"
 *       Element animates in when it scrolls into view (once).
 *   data-reveal-delay="120"        explicit delay in ms
 *   data-reveal-stagger="80"       on a PARENT → auto-delays its [data-reveal]
 *                                  children by N ms each
 *   data-parallax="0.12"           element drifts vertically as you scroll
 *
 * Accessibility: fully disabled under prefers-reduced-motion (content shows
 * immediately, no transforms). No-JS users get a <noscript> override in
 * layout/theme.liquid that un-hides everything.
 *
 * Re-callable: window.ArthausReveal.init(scope) re-arms reveals inside markup
 * injected at runtime (e.g. the PDP frame swap).
 */
window.ArthausReveal = (function () {
  'use strict';

  var reduce = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function revealAll(scope) {
    (scope || document).querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-revealed');
    });
  }

  function initReveals(scope) {
    var root = scope || document;
    var els = Array.prototype.slice.call(root.querySelectorAll('[data-reveal]:not(.is-revealed)'));
    if (!els.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      revealAll(root);
      return;
    }

    // Stagger: delay each [data-reveal] child of a [data-reveal-stagger] parent.
    root.querySelectorAll('[data-reveal-stagger]').forEach(function (group) {
      var step = parseInt(group.getAttribute('data-reveal-stagger'), 10) || 80;
      var i = 0;
      group.querySelectorAll('[data-reveal]').forEach(function (kid) {
        if (!kid.style.getPropertyValue('--reveal-delay')) {
          kid.style.setProperty('--reveal-delay', (i * step) + 'ms');
        }
        i++;
      });
    });

    // Explicit per-element delay overrides.
    els.forEach(function (el) {
      var d = el.getAttribute('data-reveal-delay');
      if (d) el.style.setProperty('--reveal-delay', parseInt(d, 10) + 'ms');
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    els.forEach(function (el) { io.observe(el); });
  }

  function initParallax(scope) {
    if (reduce) return;
    var root = scope || document;
    var els = Array.prototype.slice.call(root.querySelectorAll('[data-parallax]'));
    if (!els.length) return;

    var ticking = false;
    function update() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      els.forEach(function (el) {
        var host = el.parentElement || el;
        var rect = host.getBoundingClientRect();
        if (rect.bottom < -vh || rect.top > vh * 2) return; // off-screen, skip
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.12;
        // progress: -1 (section below) → 1 (section above), 0 ≈ centered
        var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        var offset = progress * speed * 100; // px
        el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function init(scope) {
    initReveals(scope);
    initParallax(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  return { init: init, revealAll: revealAll };
})();
